/*
 * Roomix Help Centre: self-serve return flow (inside the order view).
 * Talks only to /apps/help/return*. The email, address, phone and photos are held in memory only:
 * never in a URL, storage, cookies or logs. Labels are shown only as download links built from the latest
 * response's labelUrl (a short-lived signed link): never stored, cached or embedded.
 * All strings from the API are rendered with textContent. Reasons, fees, photo rules and terms are server-driven.
 */
(function () {
  'use strict';

  var CONTACT_URL = 'https://www.roomix.com/pages/contact';
  var EP = { create: '/apps/help/return', photo: '/apps/help/return/photo' };
  var FEDEX_URL = 'https://www.fedex.com/pudo/en-gb/';
  var UK_PC = /^[A-Za-z]{1,2}\d[A-Za-z\d]?\s*\d[A-Za-z]{2}$/;
  var MAX_PHOTOS = 5;
  var MAX_BYTES = 8 * 1024 * 1024;
  var STEPS = ['Items', 'Boxes', 'Reason', 'Terms', 'Confirm'];
  var STEP_KEYS = ['items', 'boxes', 'reasons', 'terms', 'confirm'];
  var RATE_COPY = 'Too many attempts. Please wait a few minutes and try again.';
  var ADDR_COPY = 'Please check the address. We need line 1, town or city and a valid UK postcode.';
  var BOX_PATHS = ['M3 8l9-5 9 5v8l-9 5-9-5z', 'M3 8l9 5 9-5M12 13v8'];

  var TITLES = {
    intro: 'Need to send something back?',
    items: 'Return items', boxes: 'Return items', reasons: 'Return items', terms: 'Return items', confirm: 'Return items',
    sent: 'Your return labels are ready',
    window: 'The return window has closed',
    error: 'We couldn’t create your labels'
  };

  var PHOTO_ERR = {
    file_too_large: 'That photo is too large. Please choose one under 8 MB.',
    bad_file_type: 'Please choose a JPG, PNG or HEIC photo.',
    too_many_photos: 'You can add up to 5 photos.',
    rate_limited: RATE_COPY,
    not_found: 'We couldn’t upload that photo. Please try again.'
  };

  function core() { return window.RoomixHelpCore; }
  function el(tag, cls, text) { return core().el(tag, cls, text); }
  function svg(paths, size, o) { return core().svg(paths, size, o); }
  function str(v) { return core().str(v); }
  function safeUrl(u) { return core().safeUrl(u); }
  function isMock() { return core().isMock(); }
  function qs(n) { return core().qs(n); }

  function money(n) { return '£' + Number(n).toFixed(2); }

  // A reason's fee, described from the flags the API sends: a fixed return shipping fee (returnShippingFee, in £),
  // or the older percentage restocking fee (restockingFeePercent). Null when the reason carries no fee.
  function feeOf(r) {
    if (!r) return null;
    if (Number(r.returnShippingFee) > 0) return { kind: 'ship', text: money(r.returnShippingFee) + ' return shipping fee' };
    if (Number(r.restockingFeePercent) > 0) return { kind: 'pct', text: r.restockingFeePercent + '% restocking fee' };
    return null;
  }

  function boxWord(n) { return n + (n === 1 ? ' box' : ' boxes'); }
  function labelWord(n) { return n + ' return label' + (n === 1 ? '' : 's'); }
  function uuid() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0;
      return (c === 'x' ? r : (r & 3 | 8)).toString(16);
    });
  }
  function addrString(a) {
    return [a.line1, a.line2, a.city, a.postcode].map(function (x) { return str(x).trim(); }).filter(Boolean).join(', ');
  }
  function sleep(ms, v) { return new Promise(function (r) { setTimeout(function () { r(v); }, ms); }); }

  /* ---------- mock fixtures (dev theme only) ---------- */

  function mockLabels() {
    var url = 'https://example.com/mock-return-label.pdf';
    return [
      { itemId: 'it-bed', title: 'Transition Convertible House Bed', box: 1, boxesOfItem: 2, trackingNumber: '877820435664', labelUrl: url },
      { itemId: 'it-bed', title: 'Transition Convertible House Bed', box: 2, boxesOfItem: 2, trackingNumber: '877820435675', labelUrl: url },
      { itemId: 'it-drawers', title: 'Add-On Legs and Drawers', box: 1, boxesOfItem: 1, trackingNumber: '877820435686', labelUrl: url }
    ];
  }

  function mockReturns(stage) {
    var preset = qs('return');
    var rs = qs('returnstate');
    var partial = qs('shipment') === 'partial';
    var allOut = stage === 'dispatched' || !!preset;
    var anyOut = allOut || partial;
    if (rs === 'none' || (!anyOut && rs !== 'window_closed')) return { available: false, state: 'none' };
    var reasons = [
      { code: 'changed_mind', label: 'Changed my mind', restockingFeePercent: 0, returnShippingFee: 9.99, requiresPhoto: false },
      { code: 'wrong_size_finish', label: 'Ordered the wrong size or finish', restockingFeePercent: 0, returnShippingFee: 9.99, requiresPhoto: false },
      { code: 'doesnt_fit', label: 'Doesn’t fit my space', restockingFeePercent: 0, requiresPhoto: false },
      { code: 'damaged_faulty', label: 'Arrived damaged or faulty', restockingFeePercent: 0, requiresPhoto: true },
      { code: 'missing_parts', label: 'Missing parts', restockingFeePercent: 0, requiresPhoto: true },
      { code: 'other', label: 'Other', restockingFeePercent: 0, requiresPhoto: false }
    ];
    var tableOut = allOut;
    var r = {
      available: rs !== 'window_closed',
      returnBy: 'Wed 4 Nov',
      windowLabel: '30 days',
      state: rs === 'window_closed' ? 'window_closed' : 'open',
      message: rs === 'window_closed' ? 'Returns need to be started within 30 days of dispatch, and this order was dispatched on Mon 5 Oct. If something’s wrong, or you think this is a mistake, message us with your order number and we’ll see what we can do.' : null,
      maskedEmail: 's••••@example.com',
      collection: { line1: '14 Orchard Road', line2: '', city: 'London', postcode: 'E8 3AB', phone: null },
      items: [
        { id: 'it-bed', title: 'Transition Convertible House Bed', variant: 'Natural · UK Single (190 × 90 cm)', imageUrl: null, returnable: true,
          note: 'Dispatched Mon 5 Oct · 2 boxes',
          boxes: [{ id: 'bx-bed-1', n: 1, of: 2, barcode: '5060123456789' }, { id: 'bx-bed-2', n: 2, of: 2, barcode: '5060123456802' }] },
        { id: 'it-drawers', title: 'Add-On Legs and Drawers', variant: 'Natural · UK Single (190 × 90 cm)', imageUrl: null, returnable: true,
          note: 'Dispatched Mon 5 Oct · 1 box',
          boxes: [{ id: 'bx-drawers-1', n: 1, of: 1, barcode: '5060123456819' }] },
        tableOut
          ? { id: 'it-table', title: 'Transition Bedside Table', variant: 'Natural · Single drawer', imageUrl: null, returnable: true,
              note: 'Dispatched Mon 5 Oct · 1 box', boxes: [{ id: 'bx-table-1', n: 1, of: 1, barcode: '5060123456826' }] }
          : { id: 'it-table', title: 'Transition Bedside Table', variant: 'Natural · Single drawer', imageUrl: null, returnable: false,
              note: 'Not dispatched yet. To cancel it, contact us.', boxes: [] }
      ],
      reasons: reasons,
      terms: {
        version: 'mock-v1',
        policyUrl: 'https://example.com/policies/refund-policy',
        items: [
          { key: 'window', title: 'Return window', text: 'I understand returns must be started within 30 days of dispatch, so my deadline is Wed 4 Nov.' },
          { key: 'condition', title: 'Condition', text: 'I’ll send each item back boxed securely and safe for transport.' },
          { key: 'labels', title: 'Labels', text: 'Every box has its own label. I’ll put the right label on the right box.' },
          { key: 'refund', title: 'Refund', text: 'I understand my refund is issued once my return has been received and processed, and takes 2–3 days to reach my account.' },
          { key: 'restocking', title: 'Return shipping fee', text: 'I understand a £9.99 return shipping fee will be deducted from my refund for the items listed below.', onlyIfFee: true }
        ]
      }
    };
    // Boxes already in a return (earlier in this mock session, or via ?returnstate=second|already_started).
    var done = (window.ROOMIX_HELP_MOCK_RETURNED || []).slice();
    if (rs === 'second') done = done.concat(['bx-bed-1', 'bx-drawers-1']);
    if (rs === 'already_started') r.items.forEach(function (it) { it.boxes.forEach(function (b) { done.push(b.id); }); });
    if (done.length) {
      var prev = [];
      r.items.forEach(function (it) {
        it.boxes.forEach(function (b) {
          if (done.indexOf(b.id) > -1) prev.push({ itemId: it.id, title: it.title, box: b.n, boxesOfItem: b.of, trackingNumber: '8778204356' + (60 + prev.length * 11), labelUrl: 'https://example.com/mock-return-label.pdf' });
        });
        it.boxes = it.boxes.filter(function (b) { return done.indexOf(b.id) === -1; });
        if (!it.boxes.length && it.returnable) { it.returnable = false; it.note = 'Already part of a return.'; }
      });
      r.previous = [{ reference: 'RT-10482', labelCount: prev.length, hasPhotos: false, collectionAddress: '14 Orchard Road, London, E8 3AB', labels: prev }];
      if (!r.items.some(function (it) { return it.returnable; })) {
        r.state = 'already_started';
        delete r.items; delete r.reasons; delete r.terms;
      }
    }
    return r;
  }

  function mockCall(kind, payload, data) {
    (window.ROOMIX_HELP_MOCK_LOG = window.ROOMIX_HELP_MOCK_LOG || []).push({ kind: kind, requestId: payload && payload.requestId });
    var forced = qs('returnerr');
    if (kind === 'photo') {
      var pe = qs('photoerr');
      return sleep(600).then(function () { return pe ? { status: 413, json: { ok: false, code: pe } } : { status: 200, json: { ok: true, photoId: 'ph-' + uuid().slice(0, 8) } }; });
    }
    return sleep(900).then(function () {
      if (forced === 'label_failed') return { status: 502, json: { ok: false, code: 'label_failed' } };
      var labels = [], hasPhotos = false;
      (payload.items || []).forEach(function (pi) {
        var it = null;
        data.items.forEach(function (x) { if (x.id === pi.id) it = x; });
        if (pi.photoIds && pi.photoIds.length) hasPhotos = true;
        (pi.boxes || []).forEach(function (bid) {
          var bx = null;
          it.boxes.forEach(function (b) { if (b.id === bid) bx = b; });
          (window.ROOMIX_HELP_MOCK_RETURNED = window.ROOMIX_HELP_MOCK_RETURNED || []).push(bid);
          labels.push({ itemId: it.id, title: it.title, box: bx.n, boxesOfItem: bx.of, trackingNumber: '8778204356' + (60 + labels.length * 11), labelUrl: 'https://example.com/mock-return-label.pdf' });
        });
      });
      return { status: 200, json: { ok: true, reference: 'RT-10482', labelCount: labels.length,
        collectionAddress: addrString(payload.collection), labels: labels, hasPhotos: hasPhotos } };
    });
  }

  /* ---------- network ---------- */

  function send(kind, url, body, data, isForm) {
    if (isMock()) return mockCall(kind, body, data);
    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, 45000) : null;
    var init = { method: 'POST', cache: 'no-store', credentials: 'same-origin', signal: ctrl ? ctrl.signal : undefined };
    if (isForm) { init.body = body; } else { init.headers = { 'Content-Type': 'application/json', Accept: 'application/json' }; init.body = JSON.stringify(body); }
    return fetch(url, init).then(function (res) {
      return res.json().then(function (j) { return { status: res.status, json: j }; }, function () { return { status: res.status, json: null }; });
    }).then(function (r) {
      if (timer) clearTimeout(timer);
      return r;
    }, function () {
      if (timer) clearTimeout(timer);
      return { status: 0, json: null };
    });
  }

  function codeOf(r) {
    if (r.json && typeof r.json.code === 'string') return r.json.code;
    return r.status === 404 ? 'not_found' : r.status === 429 ? 'rate_limited' : r.status === 409 ? 'not_eligible' : 'server_error';
  }

  /* ---------- photo preparation ---------- */

  function prepare(file) {
    return new Promise(function (resolve) {
      var name = file.name || 'photo';
      var type = (file.type || '').toLowerCase();
      if (!(/^image\/(jpeg|png|heic|heif)$/.test(type) || /\.(jpe?g|png|heic|heif)$/i.test(name))) return resolve({ error: 'bad_file_type' });
      function done(blob, n) { resolve(blob.size > MAX_BYTES ? { error: 'file_too_large' } : { blob: blob, name: n }); }
      var c = document.createElement('canvas');
      if (typeof createImageBitmap !== 'function' || typeof c.toBlob !== 'function') return done(file, name);
      createImageBitmap(file).then(function (bmp) {
        var scale = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
        c.width = Math.max(1, Math.round(bmp.width * scale));
        c.height = Math.max(1, Math.round(bmp.height * scale));
        c.getContext('2d').drawImage(bmp, 0, 0, c.width, c.height);
        if (bmp.close) bmp.close();
        c.toBlob(function (b) { if (b) done(b, name.replace(/\.[^.]+$/, '') + '.jpg'); else done(file, name); }, 'image/jpeg', 0.8);
      }, function () { done(file, name); }); // e.g. HEIC the browser can't decode: send as is
    });
  }

  /* ---------- mount ---------- */

  function mount(host, order, ctx) {
    var data = order && order.returns;
    if (!data) return;
    if (!(data.available === true || data.state === 'window_closed')) return;

    var items = Array.isArray(data.items) ? data.items : [];
    items.forEach(function (it) { if (!Array.isArray(it.boxes)) it.boxes = []; });
    var reasons = Array.isArray(data.reasons) ? data.reasons : [];
    var terms = data.terms && Array.isArray(data.terms.items) ? data.terms.items : [];
    var col = data.collection || {};
    var origAddr = { line1: str(col.line1), line2: str(col.line2), city: str(col.city), postcode: str(col.postcode) };

    // Earlier returns on this order, newest first. `previous` (all) is preferred; `existing` (latest only) is the older shape.
    var previous = Array.isArray(data.previous) ? data.previous : (data.existing ? [data.existing] : []);
    function toResult(ex) {
      return { reference: str(ex.reference), labelCount: ex.labelCount, collectionAddress: str(ex.collectionAddress) || addrString(origAddr),
        labels: Array.isArray(ex.labels) ? ex.labels : [], hasPhotos: ex.hasPhotos === true, nextSteps: ex.nextSteps };
    }

    var S = {
      earlier: [], returned: {},
      view: 'intro', sel: {}, box: {}, reason: {}, photos: {}, photoMsg: {}, ack: {}, note: '',
      addrEdit: !(origAddr.line1.trim() && origAddr.city.trim()), addr: { line1: origAddr.line1, line2: origAddr.line2, city: origAddr.city, postcode: origAddr.postcode },
      phone: str(col.phone), sending: false, result: null,
      requestId: '', requestKey: '', msg: '', photoSeq: 0
    };

    if (previous.length) {
      // Show the latest return's labels; "Start another return" appears if boxes remain.
      S.result = toResult(previous[0]);
      S.earlier = previous.slice(1).map(toResult);
      S.view = 'sent';
    } else if (data.state === 'window_closed') {
      S.view = 'window';
    }

    host.id = 'return';
    host.className = 'hc-card hc-return';
    while (host.firstChild) host.removeChild(host.firstChild);
    var inner = el('div', 'hc-return__inner');
    var live = el('div', 'hc-sr');
    live.setAttribute('aria-live', 'polite');
    host.appendChild(inner);
    host.appendChild(live);
    var gateFns = [];

    /* ----- derived state ----- */
    function ticked(it) { return it.boxes.filter(function (b) { return S.box[b.id]; }); }
    function active() { return items.filter(function (it) { return it.returnable !== false && S.sel[it.id] && ticked(it).length > 0; }); }
    function nLabels() { return active().reduce(function (n, it) { return n + ticked(it).length; }, 0); }
    function reasonOf(it) { var out = null; reasons.forEach(function (r) { if (r.code === S.reason[it.id]) out = r; }); return out; }
    function feeItems() { return active().filter(function (it) { return !!feeOf(reasonOf(it)); }); }
    function photoItems() { return active().filter(function (it) { var r = reasonOf(it); return r && r.requiresPhoto; }); }
    function doneCount(it) { return (S.photos[it.id] || []).filter(function (p) { return p.status === 'done'; }).length; }
    function visibleTerms() { var fees = feeItems().length > 0; return terms.filter(function (t) { return !t.onlyIfFee || fees; }); }
    function names(list) { return list.map(function (it) { return str(it.title); }).join(', '); }
    function feeLine() {
      var by = {};
      var order2 = [];
      feeItems().forEach(function (it) {
        var t = feeOf(reasonOf(it)).text;
        if (!by[t]) { by[t] = []; order2.push(t); }
        by[t].push(it);
      });
      return order2.map(function (t) { return 'A ' + t + ' will be deducted from your refund for ' + names(by[t]) + '.'; }).join(' ');
    }
    function feeHeading() {
      var kinds = {};
      feeItems().forEach(function (it) { kinds[feeOf(reasonOf(it)).kind] = true; });
      return kinds.ship && kinds.pct ? 'Fees.' : (kinds.pct ? 'Restocking fee.' : 'Return shipping fee.');
    }
    function addrOk() {
      if (!S.addrEdit) return true;
      return S.addr.line1.trim().length > 1 && S.addr.city.trim().length > 1 && UK_PC.test(S.addr.postcode.trim());
    }
    function phoneOk() { return S.phone.replace(/\D/g, '').length >= 10; }
    function curAddrString() { return addrString(S.addrEdit ? S.addr : origAddr); }

    function gateOk(view) {
      if (view === 'items') return items.some(function (it) { return it.returnable !== false && S.sel[it.id]; });
      if (view === 'boxes') return nLabels() > 0;
      if (view === 'reasons') {
        var act = active();
        if (!act.length) return false;
        return act.every(function (it) {
          var r = reasonOf(it);
          if (!r) return false;
          if (r.requiresPhoto) {
            var up = (S.photos[it.id] || []).some(function (p) { return p.status === 'uploading'; });
            return doneCount(it) > 0 && !up;
          }
          return true;
        });
      }
      if (view === 'terms') return visibleTerms().every(function (t) { return S.ack[t.key]; });
      if (view === 'confirm') return addrOk() && phoneOk() && !S.sending;
      return true;
    }

    /* ----- ui helpers ----- */
    function gateBtn(btn, ok) {
      btn.setAttribute('aria-disabled', ok ? 'false' : 'true');
    }
    function btn(label, cls, onClick, fk) {
      var b = el('button', 'hc-btn' + (cls ? ' ' + cls : ''), label);
      b.type = 'button';
      if (fk) b.setAttribute('data-fk', fk);
      b.addEventListener('click', function () { if (b.getAttribute('aria-disabled') === 'true') return; onClick(b); });
      return b;
    }
    function contactLink(label, cls) {
      var a = el('a', 'hc-btn' + (cls ? ' ' + cls : ''), label);
      a.href = CONTACT_URL;
      return a;
    }
    function check(on) {
      var s = el('span', 'hc-ck' + (on ? ' is-on' : ''));
      if (on) s.appendChild(svg(['M5 12l5 5 9-10'], 14, { sw: '3' }));
      return s;
    }
    function boxIcon(size) { var s = el('span', 'hc-ret-boxico'); s.appendChild(svg(BOX_PATHS, size || 28, { sw: '1.6' })); return s; }
    function checkRow(o) {
      var b = el('button', 'hc-ret-row' + (o.disabled ? ' is-off' : ''));
      b.type = 'button';
      b.setAttribute('role', 'checkbox');
      b.setAttribute('aria-checked', o.on ? 'true' : 'false');
      if (o.disabled) b.setAttribute('aria-disabled', 'true');
      b.setAttribute('data-fk', o.fk);
      b.appendChild(check(o.on));
      if (o.lead) b.appendChild(o.lead);
      var t = el('span', 'hc-ret-row__t');
      t.appendChild(el('span', 'hc-ret-row__title', o.title));
      if (o.sub) t.appendChild(el('span', 'hc-ret-row__sub', o.sub));
      if (o.extra) t.appendChild(el('span', 'hc-ret-row__sub', o.extra));
      if (o.note) t.appendChild(el('span', 'hc-ret-row__note', o.note));
      b.appendChild(t);
      if (!o.disabled) b.addEventListener('click', o.toggle);
      return b;
    }
    function thumb(it) {
      var d = el('span', 'hc-ret-thumb');
      var u = safeUrl(it.imageUrl);
      if (u) { var im = document.createElement('img'); im.src = u; im.alt = ''; im.width = 56; im.height = 56; im.loading = 'lazy'; d.appendChild(im); }
      return d;
    }
    function actions() { return el('div', 'hc-ret-btns'); }
    function para(text) { return el('p', 'hc-ret-p', text); }
    function panel(cls) { return el('div', 'hc-ret-panel' + (cls ? ' ' + cls : '')); }
    function strongText(parent, strongTxt, rest) {
      parent.appendChild(el('strong', null, strongTxt));
      if (rest) parent.appendChild(document.createTextNode(rest));
    }

    /* ----- navigation ----- */
    function stepIndex() { return STEP_KEYS.indexOf(S.view); }
    function go(view, focusHeading) {
      S.view = view;
      S.msg = '';
      render(focusHeading !== false);
      var i = STEP_KEYS.indexOf(view);
      if (i > -1) {
        live.textContent = '';
        setTimeout(function () { live.textContent = 'Step ' + (i + 1) + ' of 5: ' + STEPS[i]; }, 60);
      }
    }
    function prevOf(v) { return { boxes: 'items', reasons: 'boxes', terms: 'reasons', confirm: 'terms' }[v]; }
    function nextOf(v) { return { items: 'boxes', boxes: 'reasons', reasons: 'terms', terms: 'confirm' }[v]; }

    /* ----- views ----- */
    function viewIntro(body) {
      body.appendChild(para('Changed your mind, or something not right? You can start a return here for anything that has been dispatched. Choose what you’re sending back and we’ll make a return label for every box for you to download and print.'));
      var p = panel();
      p.appendChild(document.createTextNode('Return by '));
      p.appendChild(el('strong', null, str(data.returnBy)));
      p.appendChild(el('span', 'hc-ret-muted', ' · ' + str(data.windowLabel) + ' from dispatch'));
      body.appendChild(p);
      var a = actions();
      a.appendChild(btn('Start a return', 'hc-btn--primary', function () { go('items'); }, 'start'));
      a.appendChild(contactLink('Contact us instead'));
      body.appendChild(a);
    }

    function viewItems(body) {
      body.appendChild(para('Which items would you like to return? You’ll choose the boxes next.'));
      var g = el('div', 'hc-ret-group');
      g.setAttribute('role', 'group');
      g.setAttribute('aria-label', 'Items to return');
      items.forEach(function (it) {
        var ok = it.returnable !== false && it.boxes.length > 0;
        g.appendChild(checkRow({
          fk: 'item-' + it.id, on: ok && !!S.sel[it.id], disabled: !ok, lead: thumb(it),
          title: str(it.title), sub: str(it.variant), note: str(it.note),
          toggle: function () {
            if (S.sel[it.id]) { delete S.sel[it.id]; it.boxes.forEach(function (b) { delete S.box[b.id]; }); }
            else { S.sel[it.id] = true; it.boxes.forEach(function (b) { S.box[b.id] = true; }); }
            render(false);
          }
        }));
      });
      body.appendChild(g);
      var n = items.filter(function (it) { return it.returnable !== false && S.sel[it.id]; }).length;
      body.appendChild(el('div', 'hc-ret-sum', n ? n + ' selected · choose the boxes next' : 'Select at least one item to continue.'));
      var a = actions();
      var next = btn('Continue', 'hc-btn--primary', function () { go('boxes'); }, 'next');
      gateBtn(next, gateOk('items'));
      a.appendChild(next);
      a.appendChild(btn('Cancel', '', function () { go(S.result ? 'sent' : 'intro'); }, 'cancel'));
      body.appendChild(a);
    }

    function viewBoxes(body) {
      body.appendChild(para('Which boxes are you sending back? Each box you tick gets its own label. You can return part of an item, for example one of two boxes.'));
      items.filter(function (it) { return it.returnable !== false && S.sel[it.id]; }).forEach(function (it) {
        var p = el('div', 'hc-ret-card');
        p.appendChild(el('div', 'hc-ret-card__title', str(it.title)));
        var g = el('div', 'hc-ret-group');
        g.setAttribute('role', 'group');
        g.setAttribute('aria-label', 'Boxes for ' + str(it.title));
        it.boxes.forEach(function (b) {
          g.appendChild(checkRow({
            fk: 'box-' + b.id, on: !!S.box[b.id], lead: boxIcon(28),
            title: 'Box ' + str(b.n) + ' of ' + str(b.of),
            sub: str(b.barcode) ? 'Barcode ' + str(b.barcode) : '',
            toggle: function () { if (S.box[b.id]) delete S.box[b.id]; else S.box[b.id] = true; render(false); }
          }));
        });
        p.appendChild(g);
        body.appendChild(p);
      });
      var n = nLabels();
      body.appendChild(el('div', 'hc-ret-sum hc-ret-sum--plain', n ? labelWord(n) + ' · one for each box' : 'Tick at least one box to continue.'));
      var a = actions();
      var next = btn('Continue', 'hc-btn--primary', function () { go('reasons'); }, 'next');
      gateBtn(next, gateOk('boxes'));
      a.appendChild(next);
      a.appendChild(btn('Back', '', function () { go('items'); }, 'back'));
      body.appendChild(a);
    }

    function viewReasons(body) {
      body.appendChild(para('Why are you returning each item? It helps us improve.'));
      if (S.msg) { var m = el('div', 'hc-error', S.msg); m.setAttribute('role', 'alert'); body.appendChild(m); }
      var next = btn('Continue', 'hc-btn--primary', function () { go('terms'); }, 'next');
      function refreshNext() { gateBtn(next, gateOk('reasons')); }
      active().forEach(function (it) { body.appendChild(reasonPanel(it, refreshNext)); });

      var lab = el('label', 'hc-field');
      lab.appendChild(document.createTextNode('Anything else we should know? (optional)'));
      var inp = document.createElement('input');
      inp.type = 'text'; inp.value = S.note; inp.maxLength = 500;
      inp.placeholder = 'e.g. the colour looked different in our room';
      inp.autocomplete = 'off';
      inp.addEventListener('input', function () { S.note = inp.value; });
      lab.appendChild(inp);
      body.appendChild(lab);

      var a = actions();
      refreshNext();
      a.appendChild(next);
      a.appendChild(btn('Back', '', function () { go('boxes'); }, 'back'));
      body.appendChild(a);
    }

    function reasonPanel(it, refreshNext) {
      var p = el('div', 'hc-ret-card hc-ret-card--stack');
      var head = el('div');
      head.appendChild(el('div', 'hc-ret-card__title', str(it.title)));
      head.appendChild(el('div', 'hc-ret-row__sub', boxWord(ticked(it).length) + ' being returned'));
      p.appendChild(head);

      var rg = el('div', 'hc-chips');
      rg.setAttribute('role', 'radiogroup');
      rg.setAttribute('aria-label', 'Reason for returning ' + str(it.title));
      var chips = [];
      reasons.forEach(function (r) {
        var c = el('button', 'hc-chip', str(r.label));
        c.type = 'button';
        c.setAttribute('role', 'radio');
        c.setAttribute('data-fk', 'reason-' + it.id + '-' + r.code);
        c.setAttribute('aria-checked', S.reason[it.id] === r.code ? 'true' : 'false');
        c.addEventListener('click', function () {
          S.reason[it.id] = r.code;
          chips.forEach(function (x) { x.el.setAttribute('aria-checked', x.code === r.code ? 'true' : 'false'); });
          fill();
          refreshNext();
        });
        chips.push({ code: r.code, el: c });
        rg.appendChild(c);
      });
      p.appendChild(rg);

      var zone = el('div', 'hc-ret-zone');
      zone.setAttribute('role', 'status');
      p.appendChild(zone);
      var photoBox = el('div', 'hc-ret-note hc-ret-photos');
      photoBox.hidden = true;
      p.appendChild(photoBox);

      function fill() {
        var r = reasonOf(it);
        while (zone.firstChild) zone.removeChild(zone.firstChild);
        zone.className = 'hc-ret-zone';
        var fee = feeOf(r);
        if (fee) {
          zone.className = 'hc-ret-zone hc-ret-note';
          strongText(zone, 'A ' + fee.text + ' applies.', ' It will be deducted from the refund for this item.');
        } else if (r && r.requiresPhoto) {
          zone.className = 'hc-ret-zone hc-ret-note';
          strongText(zone, 'We’re sorry. Please add a photo as proof.', ' Show the damage or the missing part, and include the label on the box if you can.');
        }
        photoBox.hidden = !(r && r.requiresPhoto);
        if (r && r.requiresPhoto) buildPhotos();
      }

      var fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = 'image/jpeg,image/png,image/heic,image/heif,.jpg,.jpeg,.png,.heic,.heif';
      fileInput.multiple = true;
      fileInput.hidden = true;
      fileInput.setAttribute('tabindex', '-1');
      fileInput.setAttribute('aria-hidden', 'true');
      p.appendChild(fileInput);
      fileInput.addEventListener('change', function () {
        var files = [].slice.call(fileInput.files || []);
        fileInput.value = '';
        files.forEach(function (f) { addPhoto(f); });
      });

      function setMsg(m) { S.photoMsg[it.id] = m || ''; buildPhotos(); }

      function addPhoto(file) {
        var list = S.photos[it.id] = S.photos[it.id] || [];
        if (list.length >= MAX_PHOTOS) { setMsg(PHOTO_ERR.too_many_photos); return; }
        var rec = { key: ++S.photoSeq, name: file.name || 'photo.jpg', id: null, status: 'uploading' };
        list.push(rec);
        S.photoMsg[it.id] = '';
        buildPhotos();
        refreshNext();
        function fail(code) {
          S.photos[it.id] = (S.photos[it.id] || []).filter(function (x) { return x !== rec; });
          S.photoMsg[it.id] = PHOTO_ERR[code] || PHOTO_ERR.not_found;
          buildPhotos();
          refreshNext();
        }
        prepare(file).then(function (prep) {
          if (prep.error) return fail(prep.error);
          rec.name = prep.name;
          var fd = new FormData();
          fd.append('order', ctx.orderNo());
          fd.append('email', ctx.email());
          fd.append('itemId', it.id);
          fd.append('file', prep.blob, prep.name);
          send('photo', EP.photo, isMock() ? { itemId: it.id } : fd, data, !isMock()).then(function (r) {
            if (r.status === 200 && r.json && r.json.ok === true && r.json.photoId) {
              rec.id = String(r.json.photoId);
              rec.status = 'done';
              buildPhotos();
              refreshNext();
            } else { fail(codeOf(r)); }
          });
        });
      }

      function buildPhotos() {
        while (photoBox.firstChild) photoBox.removeChild(photoBox.firstChild);
        var list = S.photos[it.id] || [];
        if (list.length) {
          var chipsEl = el('div', 'hc-ret-photolist');
          list.forEach(function (ph) {
            var c = el('span', 'hc-photo');
            c.appendChild(el('span', 'hc-photo__name', ph.name));
            if (ph.status === 'uploading') c.appendChild(el('span', 'hc-photo__state', 'Uploading…'));
            var rm = el('button', 'hc-photo__rm', '×');
            rm.type = 'button';
            rm.setAttribute('aria-label', 'Remove ' + ph.name);
            rm.setAttribute('data-fk', 'rm-' + ph.key);
            rm.addEventListener('click', function () {
              S.photos[it.id] = (S.photos[it.id] || []).filter(function (x) { return x !== ph; });
              S.photoMsg[it.id] = '';
              buildPhotos();
              refreshNext();
            });
            c.appendChild(rm);
            chipsEl.appendChild(c);
          });
          photoBox.appendChild(chipsEl);
        }
        var full = list.length >= MAX_PHOTOS;
        var add = el('button', 'hc-btn hc-btn--white');
        add.type = 'button';
        add.setAttribute('data-fk', 'add-' + it.id);
        add.appendChild(svg(['M4 8h3l2-3h6l2 3h3v11H4z', 'M12 9.5a3.5 3.5 0 1 0 0 7 3.5 3.5 0 0 0 0-7z'], 18));
        add.appendChild(document.createTextNode(' ' + (full ? 'Maximum 5 photos' : (list.length ? 'Add another photo' : 'Add a photo'))));
        if (full) add.setAttribute('aria-disabled', 'true');
        add.addEventListener('click', function () { if (add.getAttribute('aria-disabled') === 'true') return; fileInput.click(); });
        var wrap = el('div');
        wrap.appendChild(add);
        photoBox.appendChild(wrap);
        photoBox.appendChild(el('div', 'hc-ret-hint', 'Up to 5 photos, JPG, PNG or HEIC.'));
        if (S.photoMsg[it.id]) {
          var m = el('div', 'hc-error', S.photoMsg[it.id]);
          m.setAttribute('role', 'alert');
          photoBox.appendChild(m);
        }
      }

      fill();
      return p;
    }

    function viewTerms(body) {
      body.appendChild(para('Before we send your labels, please check you’re happy with how returns work. Tick each one to confirm.'));
      var vis = visibleTerms();
      var next = btn('I understand, continue', 'hc-btn--primary', function () { go('confirm'); }, 'next');
      var g = el('div', 'hc-ret-group');
      g.setAttribute('role', 'group');
      g.setAttribute('aria-label', 'Return terms');
      vis.forEach(function (t) {
        var extra = t.onlyIfFee ? 'Applies to: ' + names(feeItems()) + '.' : '';
        g.appendChild(checkRow({
          fk: 'term-' + t.key, on: !!S.ack[t.key], title: str(t.title), sub: str(t.text), extra: extra,
          toggle: function () {
            S.ack[t.key] = !S.ack[t.key];
            render(false);
          }
        }));
      });
      body.appendChild(g);
      var pol = safeUrl(data.terms && data.terms.policyUrl);
      if (pol) {
        var a0 = el('a', 'hc-ret-link', 'Read the full returns policy');
        a0.href = pol; a0.target = '_blank'; a0.rel = 'noopener';
        a0.appendChild(el('span', 'hc-sr', ' (opens in a new tab)'));
        body.appendChild(a0);
      }
      var a = actions();
      gateBtn(next, gateOk('terms'));
      a.appendChild(next);
      a.appendChild(btn('Back', '', function () { go('reasons'); }, 'back'));
      body.appendChild(a);
    }

    function viewConfirm(body) {
      body.appendChild(para('Check everything looks right, then we’ll email your labels.'));
      var sum = el('div', 'hc-ret-sumlist');
      active().forEach(function (it) {
        var r = reasonOf(it);
        var row = el('div', 'hc-ret-sumrow');
        var l = el('div');
        l.appendChild(el('div', 'hc-ret-row__title', str(it.title)));
        l.appendChild(el('div', 'hc-ret-row__sub', r ? str(r.label) : ''));
        if (feeOf(r)) l.appendChild(el('div', 'hc-ret-extra', feeOf(r).text.replace(/^./, function (c) { return c.toUpperCase(); }) + ' applies'));
        else if (r && r.requiresPhoto) { var n = doneCount(it); l.appendChild(el('div', 'hc-ret-extra', n + (n === 1 ? ' photo attached' : ' photos attached'))); }
        row.appendChild(l);
        row.appendChild(el('div', 'hc-ret-boxes', boxWord(ticked(it).length)));
        sum.appendChild(row);
      });
      body.appendChild(sum);

      if (feeItems().length) {
        var fp = panel();
        strongText(fp, feeHeading(), ' ' + feeLine());
        body.appendChild(fp);
      }

      var cp = el('div', 'hc-ret-card hc-ret-card--stack');
      var top = el('div', 'hc-ret-coltop');
      var left = el('div', 'hc-ret-colleft');
      var pin = el('span', 'hc-ret-boxico');
      pin.appendChild(svg(['M12 21s7-6.2 7-11a7 7 0 0 0-14 0c0 4.8 7 11 7 11z', 'M12 7.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5z'], 20));
      left.appendChild(pin);
      var lt = el('div');
      lt.appendChild(el('div', 'hc-ret-row__title', 'Your parcels will go from'));
      var addrLine = el('div', 'hc-ret-addr', curAddrString());
      lt.appendChild(addrLine);
      var srcLine = el('div', 'hc-ret-row__sub', S.addrEdit && origAddr.line1.trim() ? 'Changed for this return' : 'Delivery address on your order');
      lt.appendChild(srcLine);
      left.appendChild(lt);
      top.appendChild(left);
      var tog = el('button', 'hc-btn hc-btn--white', S.addrEdit && origAddr.line1.trim() ? 'Use delivery address' : 'Change address');
      tog.type = 'button';
      tog.setAttribute('aria-expanded', S.addrEdit ? 'true' : 'false');
      tog.setAttribute('data-fk', 'addr-toggle');
      tog.addEventListener('click', function () {
        if (S.addrEdit && origAddr.line1.trim() && origAddr.city.trim()) {
          S.addr = { line1: origAddr.line1, line2: origAddr.line2, city: origAddr.city, postcode: origAddr.postcode };
          S.addrEdit = false;
        } else { S.addrEdit = true; }
        render(false);
      });
      top.appendChild(tog);
      cp.appendChild(top);

      var send1 = btn('Create my return labels', 'hc-btn--primary', doSend, 'send');
      var addrErr = el('div', 'hc-error', ADDR_COPY);
      addrErr.setAttribute('role', 'alert');
      var phoneHint = el('div', 'hc-field__help hc-err-text', 'Please enter a phone number with at least 10 digits.');
      var phoneTouched = false;
      function refreshSend() {
        var ok = gateOk('confirm');
        gateBtn(send1, ok);
        addrErr.hidden = !S.addrEdit || addrOk();
        phoneHint.hidden = !(phoneTouched && !phoneOk());
        addrLine.textContent = curAddrString();
      }

      if (S.addrEdit) {
        var form = el('div', 'hc-ret-addrform');
        var defs = [
          ['line1', 'Address line 1', 'address-line1', '14 Orchard Road'],
          ['line2', 'Address line 2 (optional)', 'address-line2', 'Flat, building, access notes'],
          ['city', 'Town or city', 'address-level2', 'London'],
          ['postcode', 'Postcode', 'postal-code', 'E8 3AB']
        ];
        defs.forEach(function (d) {
          var lb = el('label', 'hc-field');
          lb.appendChild(document.createTextNode(d[1]));
          var i = document.createElement('input');
          i.type = 'text'; i.value = S.addr[d[0]]; i.autocomplete = d[2]; i.placeholder = d[3]; i.maxLength = 120;
          if (d[0] === 'postcode') { i.autocapitalize = 'characters'; i.spellcheck = false; }
          i.addEventListener('input', function () { S.addr[d[0]] = i.value; refreshSend(); });
          lb.appendChild(i);
          form.appendChild(lb);
        });
        form.appendChild(addrErr);
        form.appendChild(el('div', 'hc-field__help', 'UK addresses only.'));
        cp.appendChild(form);
      }

      var pl = el('label', 'hc-field');
      pl.appendChild(document.createTextNode('Phone number for the FedEx driver'));
      var pi = document.createElement('input');
      pi.type = 'tel'; pi.inputMode = 'tel'; pi.autocomplete = 'tel'; pi.value = S.phone; pi.placeholder = '07700 900123'; pi.maxLength = 30;
      pi.addEventListener('input', function () { S.phone = pi.value; phoneTouched = true; refreshSend(); });
      pi.addEventListener('blur', function () { phoneTouched = true; refreshSend(); });
      pl.appendChild(pi);
      pl.appendChild(phoneHint);
      cp.appendChild(pl);
      body.appendChild(cp);

      var lp = panel('hc-ret-panel--stack');
      var l1 = el('div');
      l1.appendChild(el('strong', null, labelWord(nLabels())));
      l1.appendChild(document.createTextNode(', one for each box.'));
      lp.appendChild(l1);
      lp.appendChild(el('div', 'hc-ret-muted', 'You’ll be able to download and print them on the next screen.'));
      body.appendChild(lp);

      if (S.msg) { var m = el('div', 'hc-error', S.msg); m.setAttribute('role', 'alert'); body.appendChild(m); }

      var a = actions();
      if (S.sending) send1.textContent = 'Creating your labels…';
      a.appendChild(send1);
      a.appendChild(btn('Back', '', function () { go('terms'); }, 'back'));
      body.appendChild(a);
      refreshSend();
    }

    function buildPayload() {
      var phone = S.phone.trim();
      var coll = S.addrEdit ? S.addr : origAddr;
      return {
        order: ctx.orderNo(),
        email: ctx.email(),
        items: active().map(function (it) {
          var r = reasonOf(it);
          return {
            id: it.id, reason: r.code,
            boxes: ticked(it).map(function (b) { return b.id; }),
            photoIds: r.requiresPhoto ? (S.photos[it.id] || []).filter(function (p) { return p.status === 'done'; }).map(function (p) { return p.id; }) : []
          };
        }),
        note: S.note.trim(),
        collection: { line1: coll.line1.trim(), line2: coll.line2.trim(), city: coll.city.trim(), postcode: coll.postcode.trim().toUpperCase(), phone: phone },
        termsVersion: str(data.terms && data.terms.version),
        acknowledged: visibleTerms().filter(function (t) { return S.ack[t.key]; }).map(function (t) { return t.key; })
      };
    }

    function doSend() {
      if (S.sending || !gateOk('confirm')) return;
      var payload = buildPayload();
      var key = JSON.stringify({ i: payload.items, c: payload.collection, t: payload.termsVersion, a: payload.acknowledged, n: payload.note });
      if (!S.requestId || S.requestKey !== key) { S.requestId = uuid(); S.requestKey = key; }
      payload.requestId = S.requestId;
      S.sending = true;
      S.msg = '';
      render(false);
      send('create', EP.create, payload, data).then(function (r) {
        S.sending = false;
        var j = r.json;
        if (r.status === 200 && j && j.ok === true) {
          active().forEach(function (it) { ticked(it).forEach(function (b) { S.returned[b.id] = true; }); });
          if (S.result) S.earlier.unshift(S.result);
          S.result = j;
          go('sent');
          return;
        }
        var code = codeOf(r);
        if (code === 'already_started') { go('confirm'); ctx.relookup(); return; }
        if (code === 'not_eligible') { S.windowMsg = ''; go('window'); return; }
        if (code === 'invalid_address') { S.addrEdit = true; S.msg = ADDR_COPY; go('confirm', false); return; }
        if (code === 'photo_required') { S.msg = 'Please add a photo for each item that needs one, then continue.'; go('reasons'); return; }
        if (code === 'rate_limited') { S.msg = RATE_COPY; go('confirm', false); return; }
        go('error');
      });
    }

    function remaining() {
      return items.some(function (it) { return it.returnable !== false && it.boxes.some(function (b) { return !S.returned[b.id]; }); });
    }

    // A second return for boxes not returned yet. Boxes already in a return are removed locally;
    // the next lookup gets the same answer from the server.
    function startAnother() {
      items.forEach(function (it) {
        it.boxes = it.boxes.filter(function (b) { return !S.returned[b.id]; });
        if (!it.boxes.length && it.returnable !== false) { it.returnable = false; it.note = 'Already part of a return.'; }
      });
      S.returned = {}; S.sel = {}; S.box = {}; S.reason = {}; S.photos = {}; S.photoMsg = {}; S.ack = {};
      S.note = ''; S.requestId = ''; S.requestKey = ''; S.msg = ''; S.sending = false;
      go('items');
    }

    function labelRows(labels) {
      var list = el('div', 'hc-ret-sumlist');
      labels.forEach(function (l) {
        var row = el('div', 'hc-ret-labelrow');
        var left = el('div', 'hc-ret-labelleft');
        left.appendChild(boxIcon(28));
        var t = el('div');
        t.appendChild(el('div', 'hc-ret-row__title', str(l.title)));
        var sub = 'Box ' + str(l.box) + ' of ' + str(l.boxesOfItem);
        if (str(l.trackingNumber)) sub += ' · Tracking ' + str(l.trackingNumber);
        t.appendChild(el('div', 'hc-ret-row__sub', sub));
        left.appendChild(t);
        row.appendChild(left);
        var url = safeUrl(l.labelUrl);
        if (url) {
          // Built from the latest response only; never stored.
          var a = el('a', 'hc-btn hc-btn--primary', 'Download label');
          a.href = url;
          a.target = '_blank';
          a.rel = 'noopener';
          a.setAttribute('aria-label', 'Download label for ' + str(l.title) + ', box ' + str(l.box) + ' of ' + str(l.boxesOfItem) + ' (opens in a new tab)');
          row.appendChild(a);
        }
        list.appendChild(row);
      });
      return list;
    }

    function viewSent(body) {
      var r = S.result || {};
      var labels = Array.isArray(r.labels) ? r.labels : [];
      var n = parseInt(r.labelCount, 10) || labels.length;
      var p = el('div', 'hc-ret-lead');
      p.appendChild(document.createTextNode('Download and print '));
      p.appendChild(el('strong', null, labelWord(n)));
      p.appendChild(document.createTextNode(', one for each box. '));
      if (r.reference) p.appendChild(el('span', 'hc-ret-muted', 'Return reference ' + str(r.reference) + '.'));
      body.appendChild(p);

      body.appendChild(labelRows(labels));

      if (r.hasPhotos === true) {
        var ph = panel();
        strongText(ph, 'We’ve got your photos.', ' Our team will review them and be in touch about the damaged or missing items.');
        body.appendChild(ph);
      }

      var nx = el('div', 'hc-ret-panel hc-ret-panel--stack');
      nx.appendChild(el('h3', 'hc-gd hc-ret-next__title', 'What happens next'));
      var ol = el('ol', 'hc-ret-next');
      var steps = Array.isArray(r.nextSteps) && r.nextSteps.length ? r.nextSteps.map(function (x) { return typeof x === 'string' ? x : str(x && x.text); }) : [
        'Print each label and stick the matching one on its box. Only the boxes you chose are sent back.',
        'Arrange your FedEx collection using the button below. Have the label ready.',
        'Your refund follows once we’ve received and processed your return.'
      ];
      steps.forEach(function (x) { if (x) ol.appendChild(el('li', null, x)); });
      nx.appendChild(ol);
      body.appendChild(nx);

      if (S.earlier.length) {
        var eh = el('h3', 'hc-gd hc-ret-next__title', 'Earlier returns on this order');
        body.appendChild(eh);
        S.earlier.forEach(function (er) {
          var wrap = el('div', 'hc-ret-earlier');
          wrap.appendChild(el('div', 'hc-ret-row__sub', 'Return reference ' + str(er.reference)));
          wrap.appendChild(labelRows(Array.isArray(er.labels) ? er.labels : []));
          body.appendChild(wrap);
        });
      }

      var a2 = actions();
      if (remaining()) {
        a2.appendChild(btn('Start another return', 'hc-btn--primary', startAnother, 'another'));
        body.appendChild(el('div', 'hc-ret-sum hc-ret-sum--plain', 'Some items on this order haven\u2019t been returned. You can start a separate return for them.'));
      }
      var fx = el('a', 'hc-btn', 'Arrange your FedEx collection');
      fx.href = FEDEX_URL; fx.target = '_blank'; fx.rel = 'noopener';
      fx.appendChild(el('span', 'hc-sr', ' (opens in a new tab)'));
      a2.appendChild(fx);
      a2.appendChild(contactLink('Contact us'));
      body.appendChild(a2);
    }

    function viewWindow(body) {
      var msg = str(data.message) || 'We can’t start this return online. Message us with your order number and we’ll see what we can do.';
      body.appendChild(para(msg));
      var a = actions();
      a.appendChild(contactLink('Contact us', 'hc-btn--primary'));
      body.appendChild(a);
    }

    function viewError(body) {
      var e = el('div', 'hc-delay');
      e.setAttribute('role', 'alert');
      e.textContent = 'Something went wrong on our side and no labels were created. Your choices are saved, so you can try again. If it keeps happening, message us and we’ll send the labels ourselves.';
      body.appendChild(e);
      var a = actions();
      a.appendChild(btn('Try again', 'hc-btn--primary', function () {
        go('confirm');
      }, 'retry'));
      a.appendChild(contactLink('Contact us'));
      body.appendChild(a);
    }

    var VIEWS = { intro: viewIntro, items: viewItems, boxes: viewBoxes, reasons: viewReasons, terms: viewTerms, confirm: viewConfirm, sent: viewSent, window: viewWindow, error: viewError };

    function render(focusHeading) {
      var activeKey = document.activeElement && host.contains(document.activeElement) ? document.activeElement.getAttribute('data-fk') : null;
      while (inner.firstChild) inner.removeChild(inner.firstChild);
      var idx = stepIndex();
      var head = el('div', 'hc-return__head');
      var h2 = el('h2', 'hc-gd hc-return__title', TITLES[S.view] || TITLES.intro);
      h2.tabIndex = -1;
      head.appendChild(h2);
      if (idx > -1) head.appendChild(el('span', 'hc-return__step', 'Step ' + (idx + 1) + ' of 5'));
      inner.appendChild(head);
      if (idx > -1) {
        var st = el('div', 'hc-stepper');
        STEPS.forEach(function (label, i) {
          var seg = el('div', 'hc-stepper__seg' + (i <= idx ? ' is-on' : ''));
          seg.appendChild(el('div', 'hc-stepper__line'));
          seg.appendChild(el('div', 'hc-stepper__label', label));
          st.appendChild(seg);
        });
        inner.appendChild(st);
      }
      var body = el('div', 'hc-return__body');
      (VIEWS[S.view] || viewIntro)(body);
      inner.appendChild(body);
      if (focusHeading) { h2.focus(); }
      else if (activeKey) {
        var t = inner.querySelector('[data-fk="' + activeKey.replace(/"/g, '') + '"]');
        if (t) t.focus();
      }
    }

    applyPreset();
    render(false);

    /* ----- mock presets: jump straight to a view (matches the design's Tweaks panel) ----- */
    function applyPreset() {
      var p = isMock() ? qs('return') : '';
      if (!p) return;
      function pick(id, codeVal) { S.reason[id] = codeVal; }
      items.forEach(function (it) {
        if (it.id === 'it-bed' || it.id === 'it-drawers') { S.sel[it.id] = true; it.boxes.forEach(function (b) { S.box[b.id] = true; }); }
      });
      if (p === 'boxes') delete S.box['bx-bed-2'];
      pick('it-bed', 'changed_mind');
      pick('it-drawers', p === 'damaged' || p === 'damagedphoto' ? 'damaged_faulty' : 'doesnt_fit');
      if (p === 'damagedphoto') S.photos['it-drawers'] = [{ key: 1, name: 'IMG_1042.jpg', id: 'ph-1', status: 'done' }, { key: 2, name: 'IMG_1043.jpg', id: 'ph-2', status: 'done' }];
      if (p === 'terms') { S.ack = { window: true, condition: true, labels: true }; }
      else if (p === 'confirm' || p === 'address') { S.ack = { window: true, condition: true, labels: true, refund: true, restocking: true }; S.phone = '07700 900123'; }
      if (p === 'address') { S.addrEdit = true; S.addr = { line1: '22 Mill Lane', line2: 'Flat 2', city: 'Leeds', postcode: 'LS1 4AP' }; }
      var view = { items: 'items', boxes: 'boxes', reasons: 'reasons', damaged: 'reasons', damagedphoto: 'reasons', terms: 'terms', confirm: 'confirm', address: 'confirm', error: 'error', window: 'window' }[p];
      if (p === 'sent' || p === 'ready') {
        S.result = { reference: 'RT-10482', labelCount: 3, collectionAddress: addrString(origAddr), hasPhotos: p === 'ready', labels: mockLabels() };
        view = 'sent';
      }
      if (view) S.view = view;
    }
  }

  window.RoomixHelpReturn = { mount: mount, mockReturns: mockReturns };
})();
